import {
    MutableRefObject,
    useEffect,
    useMemo,
    useRef,
} from 'react';

import { Auth } from 'aws-amplify';
// eslint-disable-next-line import/no-extraneous-dependencies
import { CognitoUser } from 'amazon-cognito-identity-js';

import useMounted from '../useMounted';
import AuthSettingsStore from '../../../stores/AuthSettingsStore';
import UserStore from '../../../stores/UserStore';
import { CheckCodeError, ITimeoutCleaner } from '../../Common/CodeAuthenticator';
import {
    CognitoAuthError,
    OTPAuthType,
    PhoneIdentity,
    SimpleCallback,
} from '@/types/types';
import {
    captureErrorForSentry,
    captureOnce,
    retryRequest,
    SentryAdapter,
    sleep,
} from '../../utils';
import { filterCognitoSignInRetryError, generateCode, trySignUp } from './helpers';
import {
    COGNITO_CALL_RETRIES,
    NETWORK_THROTTLE_USERNAME,
    AUTH_SCHEDULE_INTERVAL,
    MAX_AUTH_SCHEDULE_DURATION_MS,
} from './constants';

export interface OTPFlowCore {
    isMountedRef: MutableRefObject<boolean>;
    checkAuthCode: (
        code: string,
        startLoading: SimpleCallback,
        stopLoading: SimpleCallback,
        onSuccess: SimpleCallback,
    ) => Promise<void>;
    sendAuthCode: (
        username: string, authType: OTPAuthType, phoneIdentity?: PhoneIdentity,
    ) => Promise<void>;
    resendTimerRef: MutableRefObject<ITimeoutCleaner>;
}

const NETWORK_TIMEOUT_MS = 5000;

const buildOtpChallengeClientMetadata = (method: OTPAuthType | null): Record<string, string> => ({
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    method: method ?? '',
});

const useOTPFlow = (
    authSettingsStore: AuthSettingsStore,
    userStore: UserStore,
): OTPFlowCore => {
    const signInScheduleRef = useRef<NodeJS.Timeout>(null);
    const scheduleStartedAtRef = useRef<number | null>(null);
    const scheduleUsernameRef = useRef<string | null>(null);
    const signedInUserRef = useRef<CognitoUser>(null);
    const lastOtpMethodRef = useRef<OTPAuthType | null>(null);
    const resendTimerRef = useRef<ITimeoutCleaner>(null);
    const isMountedRef = useMounted();

    const captureException = useMemo<SentryAdapter>(() => captureOnce(captureErrorForSentry), []);

    /** Pause the interval. Keeps refs so `visibilitychange` can resume. */
    const cancelSignInSchedule = (): void => {
        if (signInScheduleRef.current) {
            clearInterval(signInScheduleRef.current);
            signInScheduleRef.current = null;
        }
    };

    /** Terminate the flow: clears refs so `visibilitychange` cannot resurrect it. */
    const stopSignInSchedule = (): void => {
        cancelSignInSchedule();
        scheduleStartedAtRef.current = null;
        scheduleUsernameRef.current = null;
    };

    /**
     * Refresh the Cognito custom-auth session. Used as the keepalive tick
     * and as the eager refresh on `visibilitychange` -> visible. A session
     * keepalive must not outlive a real auth failure (see Sentry SPX-3644).
     */
    const fakeSignIn = async (username: string): Promise<void> => {
        try {
            signedInUserRef.current = await Auth.signIn(username);
        } catch (error) {
            console.log('error due to otp schedule', error);
            captureException(error, 'useOTPFlow.fakeSignIn');
            stopSignInSchedule();
        }
    };

    /** Start (or resume, idempotently) the keepalive interval. */
    const startInterval = (): void => {
        if (signInScheduleRef.current) return; // already running
        signInScheduleRef.current = setInterval(async () => {
            const startedAt = scheduleStartedAtRef.current;
            if (startedAt && Date.now() - startedAt >= MAX_AUTH_SCHEDULE_DURATION_MS) {
                stopSignInSchedule();
                return;
            }
            const username = scheduleUsernameRef.current;
            if (!username) {
                stopSignInSchedule();
                return;
            }
            await fakeSignIn(username);
        }, AUTH_SCHEDULE_INTERVAL);
    };

    const checkAuthCode = async (
        code: string,
        startLoading: SimpleCallback,
        stopLoading: SimpleCallback,
        onSuccess: SimpleCallback,
    ): Promise<void> => {
        stopSignInSchedule();
        let hasError = false;
        let isWrongCode = false;
        let signedInUser: CognitoUser = null;
        startLoading();
        try {
            signedInUser = await Auth.sendCustomChallengeAnswer(
                signedInUserRef.current,
                code,
                buildOtpChallengeClientMetadata(lastOtpMethodRef.current),
            );
        } catch (error) {
            console.log('could not send custom challenge answer', error);
            hasError = true;
            isWrongCode = (error as CognitoAuthError).code === 'NotAuthorizedException';
            if (!isWrongCode) {
                captureErrorForSentry(error, 'useOTPFlow.checkAuthCode');
            }
        }
        if (!hasError && !signedInUser?.getSignInUserSession()) {
            isWrongCode = true;
            hasError = true;
        } else {
            signedInUserRef.current = signedInUser;
        }
        if (hasError) {
            stopLoading();
            if (isWrongCode) {
                throw new CheckCodeError('Wrong OTP code');
            } else {
                throw new Error('Could not authenticate user');
            }
        } else {
            await userStore.subscribeOnceOnSetUser();
            stopLoading();
            onSuccess();
        }
    };

    const runSignInSchedule = (username: string): void => {
        scheduleStartedAtRef.current = Date.now();
        scheduleUsernameRef.current = username;
        startInterval();
    };

    const { API } = authSettingsStore;

    /*
     * Pause keepalive when the tab is hidden; on visible, eagerly refresh
     * the Cognito session before restarting. The eager refresh is mandatory:
     * without it, a tab hidden longer than Cognito's ~3-min session lifetime
     * returns with an expired token and the user's first submit fails with
     * `NotAuthorizedException` (mis-shown as "Wrong OTP code").
     */
    useEffect(() => {
        const onVisibilityChange = async (): Promise<void> => {
            if (document.visibilityState === 'hidden') {
                cancelSignInSchedule();
                return;
            }
            const username = scheduleUsernameRef.current;
            if (!username) return; // no active flow
            const startedAt = scheduleStartedAtRef.current;
            if (startedAt && Date.now() - startedAt >= MAX_AUTH_SCHEDULE_DURATION_MS) {
                stopSignInSchedule();
                return;
            }
            await fakeSignIn(username);
            // fakeSignIn may have called stopSignInSchedule() on error; only
            // restart the interval if the flow is still active.
            if (scheduleUsernameRef.current) {
                startInterval();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            stopSignInSchedule();
        };
    }, []);

    const sendAuthCode = async (
        username: string, authType: OTPAuthType, phoneIdentity?: PhoneIdentity,
    ): Promise<void> => {
        stopSignInSchedule();
        const usernameLower = username.toLowerCase();
        lastOtpMethodRef.current = authType;
        await generateCode(API, usernameLower, authType, phoneIdentity);
        // TODO: remove this check
        const shouldEmulateThrottling = username.includes(NETWORK_THROTTLE_USERNAME);
        if (shouldEmulateThrottling) {
            await sleep(NETWORK_TIMEOUT_MS);
        }
        await trySignUp(usernameLower, API);
        if (shouldEmulateThrottling) {
            await sleep(NETWORK_TIMEOUT_MS);
        }
        if (isMountedRef.current) {
            signedInUserRef.current = await retryRequest<CognitoUser>(
                async (): Promise<CognitoUser> => Auth.signIn(usernameLower),
                (error: unknown) => filterCognitoSignInRetryError(error as CognitoAuthError),
                COGNITO_CALL_RETRIES,
            );
        }
        if (isMountedRef.current) {
            runSignInSchedule(usernameLower);
        }
    };

    return {
        isMountedRef,
        sendAuthCode,
        checkAuthCode,
        resendTimerRef,
    };
};

export default useOTPFlow;
